import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useColors, ThemeColors } from '../../hooks/useColors';
import { spacing, typography } from '../../theme';
import {
  getTimezoneForLocation,
  getTimezoneAbbreviation,
} from '../../utils/timezone';
import { isStoreCurrentlyOpen, type StoreHours as StoreHoursType, type VendorLocation } from '../../utils/storeHours';

const DAYS_OF_WEEK = [
  { key: 'sunday', label: 'Sunday', short: 'Sun' },
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
] as const;

type DayKey = typeof DAYS_OF_WEEK[number]['key'];

interface DayHours {
  isOpen: boolean;
  openTime: string;
  closeTime: string;
  is24Hours?: boolean;
}

interface StoreHours {
  [key: string]: DayHours;
  is24_7?: boolean;
}

interface StoreHoursDisplayProps {
  hours: StoreHours | null | undefined;
  compact?: boolean;
  /** Vendor's city for timezone detection */
  vendorCity?: string | null;
  /** Vendor's state for timezone detection */
  vendorState?: string | null;
  /** Vendor's country for timezone detection */
  vendorCountry?: string | null;
}

/**
 * Format time in 12-hour format (e.g., "9:00AM")
 */
const formatTime = (time24: string): string => {
  if (!time24) return '';
  
  const parts = time24.split(':');
  if (parts.length < 2) return time24;
  
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  
  // Handle NaN or invalid values
  if (isNaN(hours) || isNaN(minutes)) return time24;
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')}${period}`;
};

/**
 * Get the current day key in a specific timezone
 * This ensures we're checking the correct day for the store's location
 */
const getCurrentDayKeyInTimezone = (storeTimezone: string): DayKey => {
  const days: DayKey[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  try {
    const now = new Date();
    // Get the day in the store's timezone
    const storeDay = new Date(now.toLocaleString('en-US', { timeZone: storeTimezone })).getDay();
    if (isNaN(storeDay) || storeDay < 0 || storeDay > 6) {
      return days[new Date().getDay()];
    }
    return days[storeDay];
  } catch {
    // Fallback to local day if timezone is invalid
    return days[new Date().getDay()];
  }
};

/**
 * Get current time in a specific timezone as HH:MM string
 */
const getCurrentTimeInTimezone = (timezone: string): string => {
  try {
    const now = new Date();
    return now.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    // Fallback to local time if timezone is invalid
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  }
};

const getNextOpenTime = (
  hours: StoreHours, 
  startDayIndex: number, 
  skipToday: boolean,
  storeTimezone: string,
  tzAbbrev: string
): string | null => {
  const dayKeys: DayKey[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Check up to 7 days ahead
  for (let i = skipToday ? 1 : 0; i < 7; i++) {
    const dayIndex = (startDayIndex + i) % 7;
    const dayKey = dayKeys[dayIndex];
    const dayHours = hours[dayKey] as DayHours | undefined;
    
    if (dayHours && dayHours.isOpen) {
      const dayLabel = i === 0 ? 'today' : i === 1 ? 'tomorrow' : dayLabels[dayIndex];
      const formattedTime = `${formatTime(dayHours.openTime)} ${tzAbbrev}`;
      
      if (i === 0) {
        return `Opens at ${formattedTime}`;
      } else if (i === 1) {
        return `Opens tomorrow at ${formattedTime}`;
      } else {
        return `Opens ${dayLabel} at ${formattedTime}`;
      }
    }
  }
  
  return null;
};

const isCurrentlyOpen = (
  hours: StoreHours | null | undefined,
  storeTimezone: string,
  tzAbbrev: string,
  vendorLocation?: VendorLocation
): { isOpen: boolean; nextChange: string | null; is24_7: boolean } => {
  if (!hours) return { isOpen: false, nextChange: null, is24_7: false };
  
  // Use the same logic as isStoreCurrentlyOpen from utils/storeHours.ts
  const storeStatus = isStoreCurrentlyOpen(hours as StoreHoursType, vendorLocation);
  
  // Check for 24/7 mode
  const is24_7 = hours.is24_7 || false;
  
  // Build nextChange message
  let nextChange: string | null = null;
  if (storeStatus.isOpen && storeStatus.closingTime) {
    // Store is open - show when it closes
    nextChange = `Closes at ${formatTime(storeStatus.closingTime)} ${tzAbbrev}`;
  } else if (!storeStatus.isOpen) {
    // Store is closed - find next open time
    const now = new Date();
    const storeNow = new Date(now.toLocaleString('en-US', { timeZone: storeTimezone }));
    const currentDayIndex = storeNow.getDay();
    nextChange = getNextOpenTime(hours, currentDayIndex, true, storeTimezone, tzAbbrev);
  }
  
  return { 
    isOpen: storeStatus.isOpen, 
    nextChange, 
    is24_7 
  };
};

export function StoreHoursDisplay({ 
  hours, 
  compact = false,
  vendorCity,
  vendorState,
  vendorCountry,
}: StoreHoursDisplayProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const [expanded, setExpanded] = useState(false);
  
  // Store timezone is based on vendor's location
  const storeTimezone = useMemo(
    () => getTimezoneForLocation(vendorCity, vendorState, vendorCountry),
    [vendorCity, vendorState, vendorCountry]
  );
  
  // Get timezone abbreviation for the store's timezone
  const tzAbbrev = useMemo(
    () => getTimezoneAbbreviation(storeTimezone),
    [storeTimezone]
  );
  
  // Vendor location for timezone-aware store hours calculation
  const vendorLocation = useMemo(
    (): VendorLocation => ({
      city: vendorCity,
      state: vendorState,
      country: vendorCountry,
    }),
    [vendorCity, vendorState, vendorCountry]
  );
  
  if (!hours || Object.keys(hours).length === 0) {
    return null;
  }
  
  const { isOpen, nextChange, is24_7 } = isCurrentlyOpen(hours, storeTimezone, tzAbbrev, vendorLocation);
  // Use current day in store's timezone for highlighting "today"
  const currentDay = getCurrentDayKeyInTimezone(storeTimezone);
  
  // Helper to get display text for a day's hours in store's timezone
  // Format: "8:00AM - 5:00PM EST"
  const getDayHoursText = (dayHours: DayHours | undefined) => {
    if (!dayHours || !dayHours.isOpen) return 'Closed';
    
    return `${formatTime(dayHours.openTime)} - ${formatTime(dayHours.closeTime)} ${tzAbbrev}`;
  };
  
  if (compact) {
    return (
      <TouchableOpacity 
        style={styles.compactContainer}
        onPress={() => setExpanded(!expanded)}
      >
        <View style={styles.compactHeader}>
          <View style={styles.statusRow}>
            <View style={[styles.statusDot, isOpen ? styles.statusOpen : styles.statusClosed]} />
            <Text style={[styles.statusText, isOpen ? styles.statusTextOpen : styles.statusTextClosed]}>
              {is24_7 ? 'Open 24/7' : isOpen ? 'Open' : 'Closed'}
            </Text>
            {!is24_7 && nextChange && (
              <Text style={styles.nextChangeText}>• {nextChange}</Text>
            )}
          </View>
          {!is24_7 && (
            <Feather 
              name={expanded ? 'chevron-up' : 'chevron-down'} 
              size={16} 
              color={colors.textLight} 
            />
          )}
        </View>
        
        {expanded && !is24_7 && (
          <View style={styles.expandedHours}>
            {DAYS_OF_WEEK.map(day => {
              const dayHours = hours[day.key] as DayHours | undefined;
              const isToday = day.key === currentDay;
              
              return (
                <View 
                  key={day.key} 
                  style={[styles.compactHourRow, isToday && styles.compactHourRowToday]}
                >
                  <Text style={[styles.compactDayText, isToday && styles.compactDayTextToday]}>
                    {day.short}
                  </Text>
                  <Text style={[
                    styles.compactHoursText, 
                    isToday && styles.compactHoursTextToday,
                    (!dayHours || !dayHours.isOpen) && styles.compactClosedText
                  ]}>
                    {getDayHoursText(dayHours)}
                  </Text>
                </View>
              );
            })}
          </View>
        )}
      </TouchableOpacity>
    );
  }
  
  // Full display
  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Feather name="clock" size={18} color={colors.text} />
        <Text style={styles.title}>Store Hours</Text>
        <View style={[styles.statusBadge, isOpen ? styles.statusBadgeOpen : styles.statusBadgeClosed]}>
          <Text style={[styles.statusBadgeText, isOpen ? styles.statusBadgeTextOpen : styles.statusBadgeTextClosed]}>
            {is24_7 ? 'Open 24/7' : isOpen ? 'Open Now' : 'Closed'}
          </Text>
        </View>
      </View>
      
      {is24_7 ? (
        <Text style={styles.fullTimeText}>This store is open 24 hours a day, 7 days a week.</Text>
      ) : (
        <View style={styles.hoursGrid}>
          {DAYS_OF_WEEK.map(day => {
            const dayHours = hours[day.key] as DayHours | undefined;
            const isToday = day.key === currentDay;
            
            return (
              <View 
                key={day.key} 
                style={[styles.hourRow, isToday && styles.hourRowToday]}
              >
                <Text style={[styles.dayText, isToday && styles.dayTextToday]}>
                  {day.label}
                </Text>
                <Text style={[
                  styles.hoursText, 
                  isToday && styles.hoursTextToday,
                  (!dayHours || !dayHours.isOpen) && styles.closedText
                ]}>
                  {getDayHoursText(dayHours)}
                </Text>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
    gap: spacing.sm,
  },
  title: {
    ...typography.subtitle,
    fontWeight: '600',
    color: colors.text,
    flex: 1,
  },
  fullTimeText: {
    ...typography.body,
    color: colors.textLight,
    textAlign: 'center',
    paddingVertical: spacing.sm,
  },
  statusBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusBadgeOpen: {
    backgroundColor: colors.success + '20',
  },
  statusBadgeClosed: {
    backgroundColor: colors.textLight + '20',
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  statusBadgeTextOpen: {
    color: colors.success,
  },
  statusBadgeTextClosed: {
    color: colors.textLight,
  },
  hoursGrid: {
    gap: spacing.xs,
  },
  hourRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 6,
  },
  hourRowToday: {
    backgroundColor: colors.primaryLight + '30',
  },
  dayText: {
    ...typography.body,
    color: colors.text,
    fontWeight: '500',
  },
  dayTextToday: {
    color: colors.primary,
    fontWeight: '600',
  },
  hoursText: {
    ...typography.body,
    color: colors.textLight,
  },
  hoursTextToday: {
    color: colors.primary,
    fontWeight: '500',
  },
  closedText: {
    color: colors.textLight,
    fontStyle: 'italic',
  },
  
  // Compact styles
  compactContainer: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    padding: spacing.sm,
  },
  compactHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusOpen: {
    backgroundColor: colors.success,
  },
  statusClosed: {
    backgroundColor: colors.textLight,
  },
  statusText: {
    ...typography.body,
    fontWeight: '600',
    fontSize: 14,
  },
  statusTextOpen: {
    color: colors.success,
  },
  statusTextClosed: {
    color: colors.textLight,
  },
  nextChangeText: {
    ...typography.caption,
    color: colors.textLight,
    marginLeft: spacing.xs,
  },
  expandedHours: {
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: 2,
  },
  compactHourRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 6,
  },
  compactHourRowToday: {
    backgroundColor: colors.primaryLight + '30',
  },
  compactDayText: {
    ...typography.body,
    color: colors.text,
    fontWeight: '500',
  },
  compactDayTextToday: {
    color: colors.primary,
    fontWeight: '600',
  },
  compactHoursText: {
    ...typography.body,
    color: colors.textLight,
  },
  compactHoursTextToday: {
    color: colors.primary,
    fontWeight: '500',
  },
  compactClosedText: {
    color: colors.textLight,
    fontStyle: 'italic',
  },
});

